import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { OnboardingTemplateActionsCell } from 'src/components/onboarding-template/grid/OnboardingTemplateActionsCell'
import { OnboardingTemplateStepsCell } from 'src/components/onboarding-template/grid/OnboardingTemplateStepsCell'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'
import { countTemplateSteps } from 'src/utils/onboardingTemplateList'
import { onboardingTemplateTableHeaderTitles } from './constants'
import { OnboardingTemplatesTableFields } from './enums'

/**
 * Wiring only — step rendering comes from OnboardingTemplateStepsCell, actions from
 * OnboardingTemplateActionsCell.
 *
 * There is deliberately NO date column: the list response carries no `createdAt` /
 * `updatedAt` — the base template DTO exposes only `id`, `name` and `partnerId` alongside the
 * nested step configs (see the design doc's §2.1) — so a "Date created" column would be empty
 * on every row.
 *
 * A factory rather than a module constant: the actions column closes over the page's open and
 * delete handlers. Callers must memoize the result, since a fresh array on every render would reset the
 * grid's column state.
 *
 * Opening a template is the pencil action only — rows are deliberately not clickable, so the
 * grid has no row-click handler and no pointer cursor.
 */
export const buildOnboardingTemplateColumns = ({
  canDeleteTemplates,
  onOpenTemplate,
  onDeleteTemplate,
}: {
  canDeleteTemplates: boolean
  onOpenTemplate(template: OnboardingTemplate): void
  onDeleteTemplate(template: OnboardingTemplate): void
}): Array<GridColDef<OnboardingTemplate>> => {
  return [
    {
      field: OnboardingTemplatesTableFields.name,
      headerName: onboardingTemplateTableHeaderTitles[OnboardingTemplatesTableFields.name],
      flex: 2,
      minWidth: 220,
      sortable: true,
      // Name identifies the row, so it is never offered for hiding in the manage-columns panel.
      hideable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<OnboardingTemplate>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {params.row.name}
          </Typography>
        )
      },
    },
    {
      field: OnboardingTemplatesTableFields.stepCount,
      headerName: onboardingTemplateTableHeaderTitles[OnboardingTemplatesTableFields.stepCount],
      width: 90,
      sortable: true,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      // `stepCount` is derived, not a row field, so the count comes off `row` — the same
      // two-parameter v8 shape `customerGrid` already uses for its display-name column.
      valueGetter: (value, row) => {
        return countTemplateSteps(row)
      },
    },
    {
      field: OnboardingTemplatesTableFields.steps,
      headerName: onboardingTemplateTableHeaderTitles[OnboardingTemplatesTableFields.steps],
      flex: 3,
      minWidth: 260,
      // A chip list has no meaningful sort order; sort by Steps or Name instead.
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<OnboardingTemplate>) => {
        return <OnboardingTemplateStepsCell template={params.row} />
      },
    },
    {
      field: OnboardingTemplatesTableFields.id,
      headerName: onboardingTemplateTableHeaderTitles[OnboardingTemplatesTableFields.id],
      width: 300,
      sortable: true,
      // Sits after the human-readable columns deliberately: Name is what an admin scans for,
      // the uuid is what they copy into a support thread.
      display: 'flex',
      renderCell: (params: GridRenderCellParams<OnboardingTemplate>) => {
        return (
          <Typography variant="body2" css={tw`truncate text-shades-500`}>
            {params.row.id}
          </Typography>
        )
      },
    },
    {
      field: OnboardingTemplatesTableFields.actions,
      headerName: onboardingTemplateTableHeaderTitles[OnboardingTemplatesTableFields.actions],
      // Matches the insurance-products and lms-courses grids, which carry the same pair of
      // icon-only tiny buttons.
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<OnboardingTemplate>) => {
        return (
          <OnboardingTemplateActionsCell
            template={params.row}
            canDelete={canDeleteTemplates}
            onOpen={onOpenTemplate}
            onDelete={onDeleteTemplate}
          />
        )
      },
    },
  ]
}
